// Release notes for HelpDesk Pro, newest first.
//
// HOW TO UPDATE: whenever you bump APP_VERSION in App.tsx, add a new entry at the
// TOP of this array with the same version string and a short, user-facing summary of
// what changed. The first entry is automatically shown as "Current".
//
// (Dates for releases before v1.8 are approximate.)

export interface ReleaseNote {
  version: string;
  date: string;          // YYYY-MM-DD
  highlights: string[];  // short, user-facing bullets
}

export const CHANGELOG: ReleaseNote[] = [
  { version: 'v1.41.2', date: '2026-08-18', highlights: [
    'Fixed a second cause of legitimate emails being ignored: messages carrying the common X-Auto-Response-Suppress header (which normal Outlook mail and forwards include) are no longer treated as automated. Only genuine auto-reply markers are skipped now.',
  ] },
  { version: 'v1.41.1', date: '2026-08-14', highlights: [
    'Fixed the email loop filter being too aggressive: legitimate messages (e.g. from senders whose mail is marked bulk) were being ignored. Intake now only skips genuine automated replies (out-of-office and RFC auto-submitted mail).',
  ] },
  { version: 'v1.41', date: '2026-07-21', highlights: [
    'Email intake now has loop protection: out-of-office and other automated replies are ignored, the helpdesk address can no longer reply to itself, and any ticket receiving a flood of emailed replies is automatically placed On Hold with an explanatory note instead of cycling endlessly.',
  ] },
  { version: 'v1.40.1', date: '2026-07-21', highlights: [
    'The Applications person filter now shows names instead of ID numbers, and resolves people who have since left.',
  ] },
  { version: 'v1.40', date: '2026-07-21', highlights: [
    'Applications can now be filtered by person (license holder), vendor, category, or application master, plus a “renewing within 90 days” toggle — all combinable. Portfolio totals stay global.',
    'Apps billed annually now show a calendar-year bar chart instead of a flat, empty-looking monthly line.',
  ] },
  { version: 'v1.39', date: '2026-07-20', highlights: [
    'Microsoft 365 licensing now syncs into Applications nightly: link an application to a tenant license SKU once (in its edit modal) and its purchased-seat count updates itself every night at 03:00 UTC, with the seat source shown as m365 plus the sync date. Every license active in the tenant is available to link.',
  ] },
  { version: 'v1.38.2', date: '2026-07-20', highlights: [
    'Store assignment moved into a pop-out: each holder card shows a compact "Stores (N)" button that opens a checklist of all stores — tick to assign, Done to close. The card face stays clean; click into a holder to see or change their stores.',
  ] },
  { version: 'v1.38.1', date: '2026-07-20', highlights: [
    'Store tagging is now multi-select: "+ stores…" on a holder card opens an inline row of all stores as toggle chips — click several in one go, then done. No more one-at-a-time dropdown.',
  ] },
  { version: 'v1.38', date: '2026-07-20', highlights: [
    'Store tracking, simplified: one global store list (the 14 Society Brands stores) now applies to every application. Each license holder gets a compact store dropdown right on their card — pick the stores they belong to, shown as chips. The per-application store panel and Access-scope setting are gone.',
  ] },
  { version: 'v1.37', date: '2026-07-20', highlights: [
    'Applications can now be flagged Organization-wide (a single license, the default) or Per-store. Per-store apps like Shopify carry a store list, and each license holder is tagged with exactly which stores they access — visible in the drill-down.',
  ] },
  { version: 'v1.36.1', date: '2026-07-20', highlights: [
    'Routing rules: agents now appear in the primary and secondary pickers (they were always accepted by the engine; the picker list was filtered too narrowly).',
  ] },
  { version: 'v1.36', date: '2026-07-20', highlights: [
    'Routing rules can now add secondary assignees: set a primary plus up to 2 secondaries on a rule, and matching tickets are staffed automatically. Secondaries get the usual notification, are never duplicated, and the 2-worker cap is enforced.',
  ] },
  { version: 'v1.35.1', date: '2026-07-15', highlights: [
    'Reports are now scoped for technicians: techs see metrics and the overview dashboard for their own assigned tickets only. Admins and viewers still see the whole helpdesk. Top Tags now also respects date, priority, and tech filters.',
  ] },
  { version: 'v1.35', date: '2026-07-15', highlights: [
    'Reports opens with a new overview dashboard: open-ticket count, 30-day SLA compliance, average first response, and weekly volume at a glance, plus charts for the 12-week trend, open tickets by age, categories, and priority mix.',
    'New report metric: Open Tickets by Age — see what is sitting unresolved and for how long.',
  ] },
  { version: 'v1.34', date: '2026-07-14', highlights: [
    'SLA bars on tickets now stop their clocks when the work is done: response freezes at first response, resolution at resolve. Finished clocks show a clear MET / BREACHED verdict instead of a percentage that keeps climbing on closed tickets.',
    'Hardware: desktop is now an asset type.',
  ] },
  { version: 'v1.33', date: '2026-07-13', highlights: [
    'Housekeeping: the retired software manager code has been fully removed from the Hardware page (software licensing has lived on the Applications page since v1.29.2), and dead imports were cleared across the app. No functional changes.',
  ] },
  { version: 'v1.32', date: '2026-07-13', highlights: [
    'Every application can now record an Application master (a HelpDesk user, distinct from the business owner) and a vendor contact (name, email, phone). Both appear in the drill-down and CSV exports, and renewal review tickets now include the vendor contact.',
  ] },
  { version: 'v1.31.1', date: '2026-07-13', highlights: [
    'Spend trends on the Applications page: each application now shows a 13-month sparkline of its AP actuals, and monthly-billed rows flag when actual spend runs meaningfully over or under the entered cost.',
  ] },
  { version: 'v1.31', date: '2026-07-13', highlights: [
    'Renewal workflow: 30 days before an application renews, HelpDesk Pro now opens a review ticket with the decision data — seats assigned vs purchased, entered cost, and last month\u2019s AP actual. Passed annual renewal dates roll forward automatically (audited). Monthly portfolio snapshots begin accruing for utilization and cost trends; trend sparklines arrive in v1.31.1.',
  ] },
  { version: 'v1.30.2', date: '2026-07-13', highlights: [
    'IT application portfolio loaded: ~28 applications now tracked with real costs, billing cycles, and renewal dates derived from AP actuals — including the RapidScale bundle (M365 + Okta + Mimecast), annual-prepay renewals for Celigo, Salesforce, and Monday.com, and history rows for vendors that stopped billing.',
  ] },
  { version: 'v1.30.1', date: '2026-07-13', highlights: [
    'Applications page now shows AP spend actuals from Definite: a "last month actual" column beside entered costs, a discovered-spend review queue (map, create, or ignore new vendors found in the IT accounts), sync provenance, and unattributed-journal visibility. Admin-only.',
  ] },
  { version: 'v1.30', date: '2026-07-13', highlights: [
    'Definite spend bridge (Stage 2, backend): AP spend actuals now sync nightly from the lakehouse. Known vendors update automatically; new vendors in the IT expense accounts queue for review. The Applications page UI for actuals and discovered spend arrives in v1.30.1.',
  ] },
  { version: 'v1.29.3', date: '2026-07-11', highlights: [
    'Sidebar reordered (Projects and Reports before Applications and Hardware) and Hardware gets a proper device icon.',
  ] },
  { version: 'v1.29.2', date: '2026-07-10', highlights: [
    'Assets is now Hardware — hardware only. Software licensing lives entirely on the Applications page, which gains the CSV import panel.',
  ] },
  { version: 'v1.29.1', date: '2026-07-10', highlights: [
    'New Applications page (admin + tech): seat utilization, license-holder drill-down with one-click reclaim of departed-user seats, renewal countdowns, and CSV export. Admins additionally see cost per app, monthly totals, and wasted-seat spend.',
  ] },
  { version: 'v1.29', date: '2026-07-10', highlights: [
    'Application spend tracking (Stage 1, backend): the software catalog now records cost (per-seat, flat, or both), seats purchased, renewal date, vendor, and owner. Financial edits are audit-logged, renewal reminders email the app owner, and adding a tracked application opens a Definite-mapping ticket automatically. The Applications page arrives in v1.29.1.',
  ] },
  { version: 'v1.28', date: '2026-06-24', highlights: [
    'Requesters now receive a confirmation email when their ticket is created — whether filed in the portal, by a tech on their behalf, or by email. Replying to the confirmation adds to the ticket.',
  ] },
  { version: 'v1.27.1', date: '2026-06-24', highlights: [
    'Fixed ticket creation failing after the new ticket-number rollout (portal and email).',
  ] },
  { version: 'v1.27', date: '2026-06-24', highlights: [
    'New ticket numbers: TK-YYYYMMDD.#### — the date plus that day\'s ticket sequence (e.g. TK-20260708.0005). Existing tickets keep their original numbers, and email replies thread correctly for both formats.',
  ] },
  { version: 'v1.26', date: '2026-06-24', highlights: [
    'Tickets can now have up to two secondary assignees (three workers total) — techs and agents can work tickets together, with everyone receiving updates and their work counted in reports.',
    'Admins and techs manage secondary assignees from the ticket sidebar ("Also working").',
  ] },
  { version: 'v1.25.3', date: '2026-06-24', highlights: [
    'Agent dashboards now show their own queue and stats (their created and assigned tickets), matching the tech experience.',
  ] },
  { version: 'v1.25.2', date: '2026-06-24', highlights: [
    'Agents can now read the public Knowledge Base, update ticket status on their tickets, and view the changelog.',
  ] },
  { version: 'v1.25.1', date: '2026-06-24', highlights: [
    'Admin \'View as\' preview now correctly simulates the Agent role, including its restricted ticket visibility.',
  ] },
  { version: 'v1.25', date: '2026-06-24', highlights: [
    'New \'Agent\' role: agents can be assigned tickets and work them with full tools (internal notes, time tracking, checklists) — but only see tickets they created or are assigned. Built for specialist team members outside core IT.',
  ] },
  { version: 'v1.24.3', date: '2026-06-24', highlights: [
    'Users who leave the company (license removed in Microsoft 365) are now retired from the helpdesk automatically by the nightly sync — and reactivated automatically if they return.',
  ] },
  { version: 'v1.24.2', date: '2026-06-24', highlights: [
    'The Microsoft 365 directory sync now runs automatically every night — job titles and manager changes in M365 appear in the helpdesk by the next morning.',
  ] },
  { version: 'v1.24.1', date: '2026-06-24', highlights: [
    'Directory sync now only imports licensed Microsoft 365 accounts, keeping service accounts and shared aliases out of the user list.',
  ] },
  { version: 'v1.24', date: '2026-06-24', highlights: [
    'HelpDesk Pro now syncs the company directory from Microsoft 365: every user gets their job title and manager attached automatically. Foundation for upcoming team/manager ticket views.',
  ] },
  { version: 'v1.23.1', date: '2026-06-24', highlights: [
    'Fixed sign-in inside the Microsoft Teams mobile app (iOS): the app now uses Teams single sign-on natively instead of a popup, so signing in no longer fails with a popup error.',
  ] },
  { version: 'v1.23', date: '2026-06-22', highlights: [
    'Knowledge Base articles can now be an uploaded PDF document, viewed inline without leaving the app — choose "Upload PDF" on the New Article screen.',
    'PDF and image attachments on tickets can now be viewed inline (click the eye icon) instead of downloading.',
  ] },
  { version: 'v1.22.1', date: '2026-06-12', highlights: [
    'Fixed: creating a ticket on behalf of another user (the requester dropdown for admins/techs) no longer fails.',
  ] },
  { version: 'v1.22', date: '2026-06-12', highlights: [
    'HelpDesk Pro is now available inside Microsoft Teams as a personal app — same app, and it signs you in automatically with your Teams account.',
  ] },
  { version: 'v1.21', date: '2026-06-10', highlights: [
    'New "Tech Stack Request" option on the New Ticket screen: opens an embedded request form. It is reviewed by the team and turned into a ticket automatically.',
  ] },
  { version: 'v1.20', date: '2026-06-09', highlights: [
    'Project tasks can now hold file attachments (admin/tech) — click the paperclip on a task to upload files; images preview inline.',
  ]},
  { version: 'v1.19', date: '2026-06-09', highlights: [
    'Project tasks can now be assigned to a tech/admin and given a due date, edited inline on each task.',
  ]},
  { version: 'v1.18', date: '2026-06-09', highlights: [
    'Projects now have a Notes thread (admin/tech) for keeping running notes on a project.',
  ]},
  { version: 'v1.17', date: '2026-06-06', highlights: [
    'Knowledge base: rename existing folders (admin/tech), and reorder articles within a folder using up/down controls.',
  ]},
  { version: 'v1.16.1', date: '2026-06-06', highlights: [
    'Removed the duplicate Software Catalog tab from the Admin area. Manage software in Assets → Software — it’s the same list.',
  ]},
  { version: 'v1.16', date: '2026-06-06', highlights: [
    'Techs now have a limited Admin area: manage the software catalog, create and edit ticket forms, and rename or retire/reactivate users. Deleting forms or users, and changing roles, remain admin-only.',
    'Fixed assigning software to a person from the Assets → Software tab — it now saves and shows the assignee.',
  ]},
  { version: 'v1.15', date: '2026-06-06', highlights: [
    'Added this Changelog page so the team can see what changed in each release.',
  ]},
  { version: 'v1.14', date: '2026-06-06', highlights: [
    'Import the software catalog from a CSV on the Assets → Software tab, with a duplicate-review step and a downloadable example file.',
  ]},
  { version: 'v1.13', date: '2026-06-06', highlights: [
    'Import hardware/assets from a CSV on the Assets → Devices tab, with duplicate review, assignee-matching by email, and a downloadable example file.',
    'Purchase date is now saved when creating or editing an asset.',
  ]},
  { version: 'v1.12.1', date: '2026-06-06', highlights: [
    'Knowledge-base articles linked to a ticket now open in a new tab without asking you to sign in again.',
  ]},
  { version: 'v1.12', date: '2026-06-06', highlights: [
    'Techs and admins can tag knowledge-base articles to a ticket — including internal articles. Tagged articles are visible only to techs and admins.',
  ]},
  { version: 'v1.11', date: '2026-06-06', highlights: [
    'Intake forms can be set to Private: hidden from end users when creating a ticket, but still usable by techs and admins.',
  ]},
  { version: 'v1.10', date: '2026-06-06', highlights: [
    'A ticket’s description can now be edited after the ticket is created.',
    'Retired users are hidden by default in the admin user list, with a toggle to show them.',
  ]},
  { version: 'v1.9.1', date: '2026-06-06', highlights: [
    'Fixed ticket file attachments not loading.',
    'Corrected the version label shown in the sidebar.',
  ]},
  { version: 'v1.9', date: '2026-06-06', highlights: [
    'Departments: assign each user to a department.',
    'Map software to departments as an onboarding reference.',
  ]},
  { version: 'v1.8', date: '2026-06-06', highlights: [
    'User management: rename a user, retire or reactivate them, or permanently delete — with ticket history preserved under a “Former User” placeholder.',
  ]},
  { version: 'v1.7', date: '2026-06-04', highlights: [
    'Dashboard cards and ticket rows are clickable for quicker navigation.',
    'Checklists update without a full page reload.',
    'Type-to-search dropdowns across the app.',
    'A “watching” section on the dashboard; internal comments and time entries are hidden from end users.',
  ]},
  { version: 'v1.6', date: '2026-06-04', highlights: [
    'Knowledge-base folder visibility controls (internal vs. public).',
    'Fixed dark-mode display issues in the knowledge base.',
    'Fixed an error when deleting a form that was already in use.',
  ]},
  { version: 'v1.5', date: '2026-06-04', highlights: [
    'Admins can “view as” other roles.',
    'Techs can retire assets.',
    'Set a category when closing a ticket.',
    '“Assign to me” on tickets.',
    'Assorted dark-mode fixes.',
  ]},
  { version: 'v1.4', date: '2026-06-03', highlights: [
    'Ticket checklists.',
    'Custom dashboard charts with drill-down.',
    'Mobile layout improvements across the app.',
  ]},
  { version: 'v1.3', date: '2026-06-03', highlights: [
    'Track the software assigned to each user in the Assets area.',
  ]},
];
